import { useRef, useEffect, useCallback, useState } from 'react';
import FJHorizontalScroller from '../../../../common/components/FJScroller/FJHorizontalScroller';
import FJTextBaseEditingRightTopContainer from '../container/FJTextBaseEditingRightTopContainer';
import styles from './FJTextBasedEditingRightTimeLine.module.less';
import PropTypes from 'prop-types';
import FJUtil from '../../../../util/FJUtil';
import FJHoverBoardGradation from '../../../../common/components/FJHoverBoard/FJHoverBoardGradation';
import FJTextBaseEditingUtil from '../../../../util/FJTextBaseEditingUtil';
import { useMemo } from 'react';
import { TEXT_BASE_EDITING_SOURCE_TYPE } from '../../../../redux/slices/FJSliceRightTextBaseEditing';

const _getNewCurrentTime = (e, boxDom, unitConversion, totalTime) => {
    const { clientX } = e;
    const { left } = boxDom.getBoundingClientRect();
    const offset = clientX - left;

    return FJUtil.getValueInRange(offset / unitConversion, 0, totalTime);
};

const FJTextBasedEditingRightTimeLine = ({
    thumbnailBlobUrl,
    updateUnitConversion,
    currentTime,
    totalTime,
    unitConversion,
    changeCurrentTime,
    splitTimeList,
    startTime,
    selectTimeList,
    isPlaying,
    sourceType,
}) => {
    const [isLoaded, setIsLoaded] = useState(false);

    const containerRef = useRef(null);
    const contentBoxRef = useRef(null);

    useEffect(() => {
        const _updateUnitConversion = e => {
            const containerWidth = containerRef.current.getScroller().offsetWidth;

            if (containerWidth) {
                updateUnitConversion(containerWidth - 28, !e ? true : false);
            }
        };

        _updateUnitConversion();

        window.addEventListener('resize', _updateUnitConversion);

        return () => {
            window.removeEventListener('resize', _updateUnitConversion);
        };
    }, [updateUnitConversion]);

    const handleMouseMove = useCallback(
        e => {
            const newCurrentTime = _getNewCurrentTime(e, contentBoxRef.current, unitConversion, totalTime);
            changeCurrentTime(newCurrentTime);
        },
        [unitConversion, changeCurrentTime, totalTime],
    );

    const handleMouseUp = useCallback(() => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
    }, [handleMouseMove]);

    const handleMouseDown = useCallback(
        e => {
            const newCurrentTime = _getNewCurrentTime(e, contentBoxRef.current, unitConversion, totalTime);

            changeCurrentTime(newCurrentTime);
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);

            return () => {
                window.removeEventListener('mousemove', handleMouseMove);
                window.removeEventListener('mouseup', handleMouseUp);
            };
        },
        [unitConversion, changeCurrentTime, handleMouseMove, handleMouseUp, totalTime],
    );

    const sourceCurrentTime = useMemo(() => {
        return FJTextBaseEditingUtil.timeLineCurrentTimeToSourceCurrentTime(currentTime, splitTimeList, startTime);
    }, [currentTime, splitTimeList, startTime]);

    useEffect(() => {
        if (containerRef.current && isPlaying) {
            const scroller = containerRef.current.getScroller();
            const containerWidth = scroller.offsetWidth;
            const scrollLeft = scroller.scrollLeft;

            const currentTimeOffset = currentTime * unitConversion;

            if (currentTimeOffset > scrollLeft + containerWidth) {
                scroller.scrollTo(
                    Math.max(currentTimeOffset - (containerWidth / 4) * 3, totalTime * unitConversion - containerWidth),
                    'smooth',
                );
            }

            if (currentTimeOffset < scrollLeft) {
                scroller.scrollTo(Math.max(0, currentTimeOffset - containerWidth / 4), 'smooth');
            }
        }
    }, [isPlaying, currentTime, unitConversion, totalTime]);

    const onLoad = useCallback(() => {
        setIsLoaded(true);
    }, []);

    useEffect(() => {
        setIsLoaded(false);
    }, [thumbnailBlobUrl]);

    return (
        <div className={styles.container}>
            <FJTextBaseEditingRightTopContainer />

            <FJHorizontalScroller className={styles.content} ref={containerRef} showScrollBar={true}>
                <div
                    className={styles.content_box}
                    ref={contentBoxRef}
                    style={{ width: `${totalTime * unitConversion}px` }}
                    onMouseDown={handleMouseDown}
                >
                    {splitTimeList.map((item, index) => (
                        <FJHoverBoardGradation
                            key={index}
                            className={styles.thumbnail}
                            contentClassName={
                                sourceType === TEXT_BASE_EDITING_SOURCE_TYPE.AUDIO && styles.thumbnail_content_audio
                            }
                            selectBgColor={sourceType === TEXT_BASE_EDITING_SOURCE_TYPE.AUDIO ? '#7defc6' : null}
                            style={{
                                width: `${(item.endTime - item.startTime) * unitConversion - (index !== 0 ? 2 : 0)}px`,
                            }}
                            isShowInBorder={true}
                            active={sourceCurrentTime >= item.startTime && sourceCurrentTime <= item.endTime}
                        >
                            <img
                                onLoad={onLoad}
                                style={{ opacity: isLoaded ? 1 : 0, left: `-${item.startTime * unitConversion}px` }}
                                src={thumbnailBlobUrl}
                                alt='thumbnail'
                            />
                        </FJHoverBoardGradation>
                    ))}

                    {selectTimeList.map((item, index) => (
                        <div
                            key={index}
                            className={styles.select_time_mask}
                            style={{
                                left: `${
                                    FJTextBaseEditingUtil.sourceCurrentTimeToTimeLineCurrentTime(
                                        item.startTime,
                                        splitTimeList,
                                        startTime,
                                    ) * unitConversion
                                }px`,
                                width: `${(item.endTime - item.startTime) * unitConversion}px`,
                            }}
                        ></div>
                    ))}

                    <div
                        className={styles.progress_line_box}
                        style={{ left: `${Math.round(currentTime * unitConversion)}px` }}
                    >
                        <div className={styles.progress_line}></div>
                    </div>
                </div>
            </FJHorizontalScroller>
        </div>
    );
};

FJTextBasedEditingRightTimeLine.propTypes = {
    thumbnailBlobUrl: PropTypes.string.isRequired,
    updateUnitConversion: PropTypes.func,
    currentTime: PropTypes.number,
    totalTime: PropTypes.number,
    unitConversion: PropTypes.number,
    changeCurrentTime: PropTypes.func,
    splitTimeList: PropTypes.array,
    startTime: PropTypes.number,
    selectTimeList: PropTypes.array,
    isPlaying: PropTypes.bool,
    sourceType: PropTypes.string,
};

export default FJTextBasedEditingRightTimeLine;
